import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// Route: /business
// Add to App.jsx or router file:
//   import JiraniBusinessPage from "./pages/JiraniBusinessPage";
//   <Route path="/business" element={<JiraniBusinessPage />} />
// Local dev: http://localhost:3000/business

const FONTS_URL =
  "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap";

function useFonts() {
  useEffect(() => {
    if (document.querySelector('link[href*="Instrument+Serif"]')) return;
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = FONTS_URL;
    document.head.appendChild(l);
  }, []);
}

const T = {
  g700: "#155c36", g600: "#1b7a47", g500: "#1e9254", g400: "#28b566",
  g200: "#a7d9bc", g100: "#d6f0e3", g50: "#eef8f3",
  n900: "#0f1117", n800: "#1c2130", n700: "#2d3549", n600: "#4a5268",
  n400: "#8a93a8", n300: "#b0b8cc", n200: "#d1d5e0", n150: "#e4e7ef",
  n100: "#eef0f5", n50: "#f7f8fa", white: "#ffffff",
  amber: "#d97706", amberBg: "#fffbeb", amberBorder: "#fde68a",
  serif: "'Instrument Serif', Georgia, serif",
  sans: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
};

const Icon = ({ name, size = 20, color = "currentColor", strokeWidth = 1.6 }) => {
  const paths = {
    ArrowLeft:     <><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></>,
    ArrowRight:    <><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></>,
    CheckCircle:   <><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></>,
    Building:      <><rect width="16" height="20" x="4" y="2" rx="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01M16 6h.01M12 6h.01M12 10h.01M8 10h.01M16 10h.01M8 14h.01M16 14h.01M12 14h.01"/></>,
    Building2:     <><path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"/><path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"/><path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 0-2 2h-2"/><path d="M10 6h4"/><path d="M10 10h4"/><path d="M10 14h4"/><path d="M10 18h4"/></>,
    LayoutDashboard:<><rect width="7" height="9" x="3" y="3" rx="1"/><rect width="7" height="5" x="14" y="3" rx="1"/><rect width="7" height="9" x="14" y="12" rx="1"/><rect width="7" height="5" x="3" y="16" rx="1"/></>,
    Users:         <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>,
    FileText:      <><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></>,
    CreditCard:    <><rect width="20" height="14" x="2" y="5" rx="2"/><path d="M2 10h20"/></>,
    Star:          <><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></>,
    ShieldCheck:   <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/></>,
    BadgeCheck:    <><path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"/><path d="m9 12 2 2 4-4"/></>,
    Clock:         <><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></>,
    Repeat:        <><path d="m17 2 4 4-4 4"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><path d="m7 22-4-4 4-4"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></>,
    MapPin:        <><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z"/><circle cx="12" cy="10" r="3"/></>,
    Zap:           <><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></>,
    MessageCircle: <><path d="m3 21 1.9-5.7a8.5 8.5 0 1 1 3.8 3.8z"/></>,
    Phone:         <><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.5a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.34 3h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 10.09a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></>,
    TrendingUp:    <><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></>,
    Home:          <><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></>,
    Hotel:         <><path d="M18 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2Z"/><path d="m9 22 .5-4h5l.5 4"/><path d="M6.4 8h11.2"/><path d="M6.4 12h11.2"/><path d="M6.4 16h11.2"/></>,
    Briefcase:     <><rect width="20" height="14" x="2" y="7" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></>,
    BarChart2:     <><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></>,
    ChevronDown:   <><path d="m6 9 6 6 6-6"/></>,
    ChevronUp:     <><path d="m18 15-6-6-6 6"/></>,
    Wrench:        <><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></>,
    Key:           <><circle cx="7.5" cy="15.5" r="5.5"/><path d="m21 2-9.6 9.6"/><path d="m15.5 7.5 3 3L22 7l-3-3"/></>,
  };
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24"
      fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      {paths[name]}
    </svg>
  );
};

/* ── Data ── */

const BUSINESS_TYPES = [
  {
    icon: "Building2",
    label: "Property managers and landlords",
    desc: "Manage maintenance across multiple units from a single dashboard. Assign jobs to vetted JiraniPros, track status per property, and get consolidated invoices at month end.",
    tags: ["Multi-unit support", "Per-property tracking", "Monthly invoicing"],
  },
  {
    icon: "Briefcase",
    label: "Corporate offices and facilities teams",
    desc: "Keep your office in order without the overhead of a facilities contractor. Book scheduled cleaning, electrical work, plumbing, and more. One account, one invoice, full visibility.",
    tags: ["Scheduled recurring jobs", "Team account access", "Priority response"],
  },
  {
    icon: "Hotel",
    label: "Hotels, Airbnbs, and short-stay rentals",
    desc: "Turnaround cleaning, maintenance between guests, and emergency repairs, all bookable on demand. Never scramble for a reliable Pro again when a guest is checking in at 2 PM.",
    tags: ["On-demand booking", "Turnaround cleans", "Emergency callouts"],
  },
  {
    icon: "Home",
    label: "SMEs and retail spaces",
    desc: "From plumbing to painting, keep your commercial space maintained without long-term service contracts. Pay a flat monthly fee and book what you need, when you need it.",
    tags: ["No long-term contracts", "Flexible scheduling", "Transparent pricing"],
  },
];

const PLATFORM_FEATURES = [
  {
    icon: "LayoutDashboard",
    title: "Central business dashboard",
    desc: "Every booking made under your business account lives in one place. See upcoming jobs, ongoing work, completed history, and pending invoices. Filter by location, service type, date, or assigned team member. Nothing falls through the cracks.",
  },
  {
    icon: "Users",
    title: "Multi-user access with role controls",
    desc: "Invite your team members to the account and set their permissions. A property manager in Westlands can book for their units; the finance lead can view invoices; the account owner controls everything. Roles are customisable.",
  },
  {
    icon: "FileText",
    title: "Monthly invoicing and receipts",
    desc: "Instead of paying per job through M-Pesa one transaction at a time, Business accounts are billed monthly. One invoice, clearly itemised by date, job type, property, and JiraniPro. Download as PDF or receive by email automatically.",
  },
  {
    icon: "Repeat",
    title: "Recurring job scheduling",
    desc: "Set jobs to repeat automatically. Weekly office cleaning, fortnightly garden maintenance, monthly generator servicing. Choose the frequency, confirm the JiraniPro, and it runs without you having to rebook each time.",
  },
  {
    icon: "BadgeCheck",
    title: "Business-tier verified JiraniPros",
    desc: "Business accounts are matched with JiraniPros who have completed additional vetting and have a track record of reliability. These are professionals with consistent ratings above 4.5 and a history of handling commercial work.",
  },
  {
    icon: "Phone",
    title: "Dedicated account support line",
    desc: "Business accounts have access to a dedicated support contact. Not a chatbot, not a ticketing queue. A real person who knows your account and can resolve issues, reschedule jobs, or assist with disputes directly.",
  },
  {
    icon: "BarChart2",
    title: "Spending and usage reports",
    desc: "Monthly and quarterly reports on your maintenance spend, broken down by service category, property, and JiraniPro. Useful for budgeting, audit trails, and identifying where recurring issues are costing you most.",
  },
  {
    icon: "MapPin",
    title: "Multi-location management",
    desc: "Manage services across different estates, buildings, or cities in one account. Each location is labelled and filterable. Book a plumber in Nyali and a cleaner in Kilimani in the same session, from the same screen.",
  },
];

const PLANS = [
  {
    name: "Starter",
    price: "KES 3,500",
    period: "per month",
    desc: "For small landlords and SMEs with straightforward maintenance needs.",
    highlight: false,
    features: [
      "Up to 2 team members",
      "Up to 10 bookings per month",
      "Central dashboard",
      "Monthly invoice",
      "Standard JiraniPro matching",
      "In-app support",
    ],
  },
  {
    name: "Business",
    price: "KES 8,500",
    period: "per month",
    desc: "For property managers, Airbnbs, and offices that book regularly.",
    highlight: true,
    badge: "Most popular",
    features: [
      "Up to 8 team members",
      "Unlimited bookings",
      "Central dashboard with filters",
      "Recurring job scheduling",
      "Business-tier JiraniPros",
      "Monthly invoice + PDF download",
      "Multi-location support",
      "Priority support line",
    ],
  },
  {
    name: "Enterprise",
    price: "Custom",
    period: "talk to us",
    desc: "For hotel groups, large facilities teams, and multi-city operations.",
    highlight: false,
    features: [
      "Unlimited team members",
      "Unlimited bookings",
      "Everything in Business",
      "Quarterly spend reports",
      "Custom role permissions",
      "Dedicated account manager",
      "SLA-backed response times",
      "API access on request",
    ],
  },
];

const HOW_IT_WORKS = [
  {
    num: "01",
    icon: "Briefcase",
    title: "Register your business account",
    desc: "Sign up with your business name, phone number, and a work email. Choose the plan that fits your team size and booking volume. No credit card needed to get started; your first invoice arrives at end of month.",
    chips: ["Under 5 minutes", "No upfront payment", "Instant access"],
  },
  {
    num: "02",
    icon: "Users",
    title: "Set up your team and locations",
    desc: "Add team members by phone number and assign their access level. Add each property or location you manage with a label, address, and any recurring access notes. Everything is editable later.",
    chips: ["Custom roles", "Unlimited locations", "Access notes per site"],
  },
  {
    num: "03",
    icon: "Repeat",
    title: "Book services and set recurring jobs",
    desc: "Book a one-off job or schedule recurring work. Select the service, date, time, location, and any specific instructions. The matched JiraniPro receives the booking immediately and confirms within the hour. For recurring jobs, confirmation is done once.",
    chips: ["On-demand or scheduled", "Same-day available", "Instant Pro notification"],
  },
  {
    num: "04",
    icon: "LayoutDashboard",
    title: "Track everything from your dashboard",
    desc: "Every active, upcoming, and completed job appears in your business dashboard in real time. See who is on site, when they arrived, what was done, and any notes or photos uploaded by the JiraniPro after completion. Your team sees what is relevant to them.",
    chips: ["Live status updates", "Post-job notes", "Team visibility controls"],
  },
  {
    num: "05",
    icon: "FileText",
    title: "Receive one invoice at month end",
    desc: "At the end of each billing cycle, you receive a single itemised invoice covering every job booked under your account. Each line item shows the date, service type, property, JiraniPro name, and amount. Download as PDF, forward to accounts, done.",
    chips: ["Itemised by job", "PDF download", "Email delivery"],
  },
];

const FAQS = [
  {
    q: "Can I switch plans or cancel at any time?",
    a: "Yes. You can upgrade, downgrade, or cancel your Business account from the account settings page. Changes take effect at the start of the next billing cycle. If you cancel, your account remains active until the end of the current paid period and you will not be billed again.",
  },
  {
    q: "How does the monthly invoice work for accounting?",
    a: "At the end of each calendar month, a full invoice is generated and sent to the email on file for your account. The invoice is itemised by job, showing the date, service type, property or location label, the JiraniPro assigned, and the amount. It is downloadable as a PDF. For businesses on Starter who exceed 10 bookings, additional jobs are billed at standard per-job rates and reflected on the same invoice.",
  },
  {
    q: "What qualifies a JiraniPro as business-tier?",
    a: "Business-tier JiraniPros have a sustained rating of 4.5 stars or higher over a minimum of 30 completed jobs, a track record of reliability including low cancellation and no-show rates, and have been flagged by our team as suitable for commercial work. They are available exclusively to Business and Enterprise plan accounts.",
  },
  {
    q: "Can multiple team members book at the same time?",
    a: "Yes. Each team member with booking permissions can make bookings independently and simultaneously. All bookings appear in the central dashboard regardless of who made them, labelled with the team member's name. There is no conflict or queue between users.",
  },
  {
    q: "What happens if a booked JiraniPro is unavailable?",
    a: "If a confirmed JiraniPro becomes unavailable, your dedicated support contact will notify your account and work to confirm an equally qualified replacement as quickly as possible. Business accounts are prioritised for rematch, which typically happens within 1 to 2 hours for scheduled work and within 30 minutes for same-day urgent jobs.",
  },
  {
    q: "Is there a minimum contract period?",
    a: "No. Business plans are billed monthly with no minimum term. You are not locked in. Enterprise accounts may negotiate custom terms depending on the arrangement, but this is agreed upfront and always documented in writing.",
  },
  {
    q: "How are disputes handled for business accounts?",
    a: "Business accounts have access to a dedicated support contact who handles disputes directly rather than routing them through the standard queue. If a job was not completed to standard, raise it within 48 hours. Your support contact will review the job record, including any photos and messages, and work toward resolution, including partial or full refunds where warranted.",
  },
  {
    q: "Can I restrict which services my team members can book?",
    a: "On Enterprise accounts, role permissions can be customised to limit which service categories, locations, or spend levels a team member can book. On Starter and Business plans, booking permissions are either full or none. Custom permission scoping is available on Enterprise.",
  },
];

/* ── FAQ item ── */
function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: `1px solid ${T.n100}` }}>
      <button onClick={() => setOpen(o => !o)} style={{
        width: "100%", textAlign: "left", background: "none", border: "none",
        padding: "22px 0", display: "flex", justifyContent: "space-between",
        alignItems: "center", cursor: "pointer", gap: 20, fontFamily: T.sans,
      }}>
        <span style={{ fontSize: 15, fontWeight: 400, color: T.n800, lineHeight: 1.5, letterSpacing: "-0.01em" }}>{q}</span>
        <span style={{
          width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
          border: `1px solid ${open ? T.g500 : T.n200}`,
          background: open ? T.g500 : "transparent",
          color: open ? T.white : T.n400,
          display: "flex", alignItems: "center", justifyContent: "center",
          transition: "all .22s cubic-bezier(.4,0,.2,1)",
          transform: open ? "rotate(45deg)" : "none",
          fontSize: 18, fontWeight: 300, lineHeight: 1,
        }}>+</span>
      </button>
      <div style={{ overflow: "hidden", maxHeight: open ? 400 : 0, transition: "max-height .32s cubic-bezier(.4,0,.2,1)" }}>
        <p style={{ margin: "0 0 22px", fontSize: 14, color: T.n600, lineHeight: 1.8, paddingRight: 48, fontFamily: T.sans }}>{a}</p>
      </div>
    </div>
  );
}

/* ── How it works step ── */
function WorkStep({ step, isLast }) {
  return (
    <div style={{ display: "flex", gap: 20, position: "relative" }}>
      {!isLast && (
        <div style={{ position: "absolute", left: 23, top: 52, bottom: -16, width: 1, background: `linear-gradient(to bottom, ${T.g200}, ${T.n100})` }} />
      )}
      <div style={{ width: 48, height: 48, borderRadius: 14, background: T.g50, border: `1.5px solid ${T.g100}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, zIndex: 1 }}>
        <Icon name={step.icon} size={20} color={T.g500} strokeWidth={1.6} />
      </div>
      <div style={{ flex: 1, paddingBottom: isLast ? 0 : 44 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: T.g500, letterSpacing: "0.1em", marginBottom: 5 }}>STEP {step.num}</div>
        <h3 style={{ fontFamily: T.serif, fontSize: "clamp(18px,2.8vw,22px)", fontWeight: 400, color: T.n900, margin: "0 0 10px", letterSpacing: "-0.02em", lineHeight: 1.2 }}>{step.title}</h3>
        <p style={{ fontSize: 14, color: T.n600, lineHeight: 1.78, margin: "0 0 14px" }}>{step.desc}</p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {step.chips.map((c, i) => (
            <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, color: T.g700, background: T.g50, border: `1px solid ${T.g100}`, borderRadius: 99, padding: "4px 11px", fontWeight: 500, fontFamily: T.sans }}>
              <Icon name="CheckCircle" size={12} color={T.g500} strokeWidth={2.2} />
              {c}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Plan card ── */
function PlanCard({ plan, onContact, onSignUp }) {
  const isEnterprise = plan.name === "Enterprise";
  return (
    <div style={{
      flex: "1 1 240px", borderRadius: 18, padding: "28px 24px",
      border: plan.highlight ? `2px solid ${T.g500}` : `1.5px solid ${T.n150}`,
      background: plan.highlight ? T.g500 : T.white,
      display: "flex", flexDirection: "column", gap: 20, position: "relative",
    }}>
      {plan.badge && (
        <div style={{ position: "absolute", top: -12, left: "50%", transform: "translateX(-50%)", background: T.g700, color: T.white, fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", padding: "4px 14px", borderRadius: 99, whiteSpace: "nowrap", fontFamily: T.sans }}>
          {plan.badge}
        </div>
      )}
      <div>
        <div style={{ fontSize: 12, fontWeight: 700, color: plan.highlight ? T.g100 : T.g500, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8 }}>{plan.name}</div>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 6, marginBottom: 8 }}>
          <span style={{ fontFamily: T.serif, fontSize: "clamp(28px,4vw,36px)", fontWeight: 400, color: plan.highlight ? T.white : T.n900, letterSpacing: "-0.03em", lineHeight: 1 }}>{plan.price}</span>
          <span style={{ fontSize: 13, color: plan.highlight ? T.g200 : T.n400, marginBottom: 4 }}>{plan.period}</span>
        </div>
        <p style={{ fontSize: 13, color: plan.highlight ? T.g100 : T.n600, lineHeight: 1.65, margin: 0 }}>{plan.desc}</p>
      </div>
      <div style={{ borderTop: `1px solid ${plan.highlight ? T.g400 : T.n100}`, paddingTop: 20, display: "flex", flexDirection: "column", gap: 10 }}>
        {plan.features.map((f, i) => (
          <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
            <div style={{ flexShrink: 0, marginTop: 1 }}>
              <Icon name="CheckCircle" size={15} color={plan.highlight ? T.g100 : T.g500} strokeWidth={2} />
            </div>
            <span style={{ fontSize: 13, color: plan.highlight ? T.g50 : T.n700, lineHeight: 1.5 }}>{f}</span>
          </div>
        ))}
      </div>
      <button
        onClick={isEnterprise ? onContact : onSignUp}
        style={{
          marginTop: "auto", padding: "13px", borderRadius: 10, fontSize: 14, fontWeight: 600,
          fontFamily: T.sans, cursor: "pointer", letterSpacing: "0.01em",
          border: plan.highlight ? "none" : `1.5px solid ${T.n200}`,
          background: plan.highlight ? T.white : "transparent",
          color: plan.highlight ? T.g600 : T.n700,
          transition: "all .18s",
        }}
        onMouseEnter={e => {
          if (plan.highlight) { e.currentTarget.style.background = T.g50; }
          else { e.currentTarget.style.borderColor = T.n400; e.currentTarget.style.color = T.n900; }
        }}
        onMouseLeave={e => {
          if (plan.highlight) { e.currentTarget.style.background = T.white; }
          else { e.currentTarget.style.borderColor = T.n200; e.currentTarget.style.color = T.n700; }
        }}>
        {isEnterprise ? "Talk to our team" : plan.highlight ? "Get started free" : "Start with " + plan.name}
      </button>
    </div>
  );
}

/* ── Business type card ── */
function BusinessTypeCard({ icon, label, desc, tags }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: "24px", borderRadius: 16,
        border: `1.5px solid ${hovered ? T.g200 : T.n150}`,
        background: hovered ? T.g50 : T.white,
        transition: "all .18s", display: "flex", flexDirection: "column", gap: 14,
      }}>
      <div style={{ width: 44, height: 44, borderRadius: 12, background: hovered ? T.g100 : T.n50, border: `1px solid ${hovered ? T.g200 : T.n150}`, display: "flex", alignItems: "center", justifyContent: "center", transition: "all .18s" }}>
        <Icon name={icon} size={20} color={hovered ? T.g600 : T.n400} strokeWidth={1.6} />
      </div>
      <div>
        <div style={{ fontSize: 15, fontWeight: 600, color: T.n900, marginBottom: 8, letterSpacing: "-0.01em" }}>{label}</div>
        <p style={{ fontSize: 13, color: T.n600, lineHeight: 1.75, margin: "0 0 14px" }}>{desc}</p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {tags.map((t, i) => (
            <span key={i} style={{ fontSize: 11, color: hovered ? T.g700 : T.n500, background: hovered ? T.white : T.n50, border: `1px solid ${hovered ? T.g100 : T.n150}`, borderRadius: 99, padding: "3px 10px", fontWeight: 500, fontFamily: T.sans, transition: "all .18s" }}>{t}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ══ MAIN PAGE ══ */
export default function JiraniBusinessPage() {
  useFonts();
  const navigate = useNavigate();

  const goToSignUp  = () => navigate("/signup?type=business");
  const goToContact = () => navigate("/contact?subject=enterprise");

  return (
    <div style={{ fontFamily: T.sans, background: T.white, minHeight: "100vh", color: T.n900, paddingBottom: 80 }}>
      <style>{`
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { display: none; }
      `}</style>

      {/* ── Nav ── */}
      <div style={{ padding: "16px 24px", borderBottom: `1px solid ${T.n100}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <button onClick={() => navigate(-1)} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", color: T.n400, fontSize: 13, fontFamily: T.sans, padding: 0 }}
          onMouseEnter={e => e.currentTarget.style.color = T.n800}
          onMouseLeave={e => e.currentTarget.style.color = T.n400}>
          <Icon name="ArrowLeft" size={14} strokeWidth={2} />
          Back
        </button>
        <button onClick={goToContact} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: `1px solid ${T.n200}`, cursor: "pointer", color: T.n600, fontSize: 13, fontFamily: T.sans, padding: "8px 16px", borderRadius: 8, fontWeight: 500 }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = T.n400; e.currentTarget.style.color = T.n900; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = T.n200; e.currentTarget.style.color = T.n600; }}>
          <Icon name="Phone" size={13} strokeWidth={2} />
          Talk to our team
        </button>
      </div>

      {/* ══ HERO ══ */}
      <div style={{ padding: "clamp(56px,8vw,100px) 20px clamp(48px,6vw,80px)", borderBottom: `1px solid ${T.n100}` }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, color: T.g600, fontSize: 12, fontWeight: 600, letterSpacing: "0.09em", textTransform: "uppercase", marginBottom: 24 }}>
            <Icon name="Briefcase" size={14} color={T.g500} strokeWidth={2.2} />
            JiraniApp for Business
          </div>
          <h1 style={{ fontFamily: T.serif, fontSize: "clamp(40px,7.5vw,70px)", fontWeight: 400, margin: "0 0 22px", lineHeight: 1.05, color: T.n900, letterSpacing: "-0.03em", maxWidth: 660 }}>
            Home services at scale,<br/>
            <em style={{ fontStyle: "italic", color: T.g600 }}>built for businesses</em>
          </h1>
          <p style={{ fontSize: "clamp(15px,2vw,17px)", color: T.n600, lineHeight: 1.75, margin: "0 0 36px", maxWidth: 520 }}>
            One account for your whole team. Book verified JiraniPros across all your properties, offices, or units. Track every job in real time. Get one invoice at month end.
          </p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
            <button onClick={goToSignUp} style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              background: T.g500, color: T.white, border: "none",
              padding: "15px 30px", borderRadius: 11, fontSize: 15, fontWeight: 600,
              cursor: "pointer", fontFamily: T.sans, letterSpacing: "0.01em",
            }}
              onMouseEnter={e => e.currentTarget.style.background = T.g600}
              onMouseLeave={e => e.currentTarget.style.background = T.g500}>
              Create a business account
              <Icon name="ArrowRight" size={16} color={T.white} strokeWidth={2} />
            </button>
            <button onClick={goToContact} style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "none", border: `1.5px solid ${T.n200}`, padding: "14px 24px", borderRadius: 11, fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: T.sans, color: T.n700 }}
              onMouseEnter={e => e.currentTarget.style.borderColor = T.n400}
              onMouseLeave={e => e.currentTarget.style.borderColor = T.n200}>
              Book a demo
            </button>
          </div>
        </div>
      </div>

      {/* ══ TRUST STRIP ══ */}
      <div style={{ borderBottom: `1px solid ${T.n100}`, padding: "0 20px" }}>
        <div style={{ maxWidth: 720, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
          {[
            { icon: "BadgeCheck", label: "Business-tier verified JiraniPros" },
            { icon: "FileText",   label: "Single monthly invoice, fully itemised" },
            { icon: "Users",      label: "Multi-user team access with role controls" },
          ].map(({ icon, label }, i) => (
            <div key={i} style={{ padding: "22px 20px", display: "flex", alignItems: "center", gap: 12, borderRight: i < 2 ? `1px solid ${T.n100}` : "none" }}>
              <Icon name={icon} size={18} color={T.g500} strokeWidth={1.5} />
              <span style={{ fontSize: 13, color: T.n600, lineHeight: 1.4 }}>{label}</span>
            </div>
          ))}
        </div>
      </div>

      <div style={{ maxWidth: 720, margin: "0 auto", padding: "0 20px" }}>

        {/* ══ WHO IT IS FOR ══ */}
        <div style={{ paddingTop: "clamp(48px,7vw,72px)", marginBottom: "clamp(48px,7vw,72px)" }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 12px" }}>Who it is for</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4.5vw,36px)", fontWeight: 400, color: T.n900, margin: "0 0 8px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            Built for every business that <em style={{ fontStyle: "italic", color: T.g600 }}>manages a space</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 28px", lineHeight: 1.65 }}>
            Whether you run one Airbnb or forty rental units, a small office or a hotel, JiraniApp for Business is designed around the way you actually work.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
            {BUSINESS_TYPES.map((b, i) => <BusinessTypeCard key={i} {...b} />)}
          </div>
        </div>

        {/* ══ HOW IT WORKS ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,72px)" }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 12px" }}>How it works</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4.5vw,36px)", fontWeight: 400, color: T.n900, margin: "0 0 8px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            Set up once, run <em style={{ fontStyle: "italic", color: T.g600 }}>indefinitely</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 36px", lineHeight: 1.65 }}>
            Most business accounts are fully configured within an hour. After that, booking is as simple as selecting a service and confirming a time.
          </p>
          <div style={{ paddingLeft: 4 }}>
            {HOW_IT_WORKS.map((step, i) => (
              <WorkStep key={i} step={step} isLast={i === HOW_IT_WORKS.length - 1} />
            ))}
          </div>
        </div>

        {/* ══ PLATFORM FEATURES ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,72px)" }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 12px" }}>Platform features</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4.5vw,36px)", fontWeight: 400, color: T.n900, margin: "0 0 8px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            Everything your team <em style={{ fontStyle: "italic", color: T.g600 }}>actually needs</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 28px", lineHeight: 1.65 }}>
            The Business dashboard is purpose-built for teams that manage ongoing maintenance across multiple locations, not a regular client account with extra users bolted on.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
            {PLATFORM_FEATURES.map((f, i) => (
              <div key={i} style={{ display: "flex", gap: 20, padding: "22px 0", borderBottom: i < PLATFORM_FEATURES.length - 1 ? `1px solid ${T.n100}` : "none" }}>
                <div style={{ width: 42, height: 42, borderRadius: 11, background: T.n50, border: `1px solid ${T.n150}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
                  <Icon name={f.icon} size={18} color={T.g500} strokeWidth={1.6} />
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: T.n900, marginBottom: 6, letterSpacing: "-0.01em" }}>{f.title}</div>
                  <p style={{ fontSize: 14, color: T.n600, lineHeight: 1.75, margin: 0 }}>{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ══ PLANS ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,72px)" }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 12px" }}>Pricing</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4.5vw,36px)", fontWeight: 400, color: T.n900, margin: "0 0 8px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            One flat monthly fee, <em style={{ fontStyle: "italic", color: T.g600 }}>no surprises</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 32px", lineHeight: 1.65 }}>
            Your subscription covers the platform. Jobs are billed on top at transparent per-job rates, clearly listed on your invoice each month.
          </p>
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "stretch" }}>
            {PLANS.map((plan, i) => (
              <PlanCard key={i} plan={plan} onSignUp={goToSignUp} onContact={goToContact} />
            ))}
          </div>
          <div style={{ marginTop: 20, padding: "16px 20px", borderRadius: 12, background: T.amberBg, border: `1px solid ${T.amberBorder}` }}>
            <p style={{ fontSize: 13, color: "#92400e", margin: 0, lineHeight: 1.7, fontFamily: T.sans }}>
              All plans include a 14-day free trial with no billing until the trial ends. Individual job rates depend on service type and are shown before booking confirmation. Platform subscription fees do not replace per-job charges.
            </p>
          </div>
        </div>

        {/* ══ TRUST BLOCK ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,72px)", padding: "clamp(28px,5vw,44px)", borderRadius: 20, background: T.g50, border: `1px solid ${T.g100}` }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 7, color: T.g600, fontSize: 11, fontWeight: 600, letterSpacing: "0.09em", textTransform: "uppercase", marginBottom: 14 }}>
            <Icon name="ShieldCheck" size={13} color={T.g500} strokeWidth={2} />
            Business-grade trust
          </div>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(22px,4vw,30px)", fontWeight: 400, color: T.n900, margin: "0 0 10px", letterSpacing: "-0.03em", lineHeight: 1.2 }}>
            Your properties, your team, <em style={{ fontStyle: "italic", color: T.g600 }}>protected</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n600, lineHeight: 1.75, margin: "0 0 28px" }}>
            Every JiraniPro that appears on a Business account booking has been individually verified, reviewed for commercial suitability, and maintains a sustained rating above 4.5 stars. Beyond the Pro, the booking itself carries full platform protection.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 14 }}>
            {[
              { icon: "Key",         title: "NDA-ready professionals", desc: "JiraniPros working corporate or hospitality accounts can sign NDAs on request through your account manager." },
              { icon: "ShieldCheck", title: "Damage cover on every job", desc: "Business bookings carry the same Jirani Guarantee as standard bookings, with priority resolution handling." },
              { icon: "Clock",       title: "Documented job records", desc: "Every job generates a timestamped record including arrival, completion, Pro notes, and any uploaded photos." },
              { icon: "TrendingUp",  title: "Quarterly performance reports", desc: "See your maintenance spend trends, most-used service types, and Pro reliability scores over time." },
            ].map((item, i) => (
              <div key={i} style={{ padding: "18px", borderRadius: 14, background: T.white, border: `1px solid ${T.g100}`, display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: T.g50, border: `1px solid ${T.g100}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Icon name={item.icon} size={16} color={T.g500} strokeWidth={1.7} />
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.n900, letterSpacing: "-0.01em" }}>{item.title}</div>
                <p style={{ fontSize: 12, color: T.n600, lineHeight: 1.7, margin: 0 }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ══ FAQ ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,72px)" }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 12px" }}>Business FAQ</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4.5vw,36px)", fontWeight: 400, color: T.n900, margin: "0 0 4px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            Questions from <em style={{ fontStyle: "italic", color: T.g600 }}>business clients</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 8px", lineHeight: 1.65 }}>
            Answers to what operations and facilities teams ask most before signing up.
          </p>
          <div>
            {FAQS.map((item, i) => <FaqItem key={i} {...item} />)}
          </div>
        </div>

        {/* ══ FOOTER CTA ══ */}
        <div style={{ padding: "clamp(36px,5vw,56px)", borderRadius: 20, border: `1px solid ${T.n150}`, textAlign: "center", background: T.n50, marginBottom: 20 }}>
          <div style={{ width: 52, height: 52, borderRadius: 14, background: T.g50, border: `1.5px solid ${T.g200}`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
            <Icon name="Building2" size={22} color={T.g500} strokeWidth={1.6} />
          </div>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(28px,4.5vw,40px)", fontWeight: 400, color: T.n900, margin: "0 0 12px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            Your properties deserve<br/>
            <em style={{ fontStyle: "italic", color: T.g600 }}>better maintenance</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 auto 32px", lineHeight: 1.75, maxWidth: 420 }}>
            Start your 14-day free trial. No billing until the trial ends, no contract to sign, and a real person to help you get set up.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={goToSignUp} style={{
              padding: "14px 32px", background: T.g500, color: T.white,
              border: "none", borderRadius: 11, fontSize: 15, fontWeight: 600,
              fontFamily: T.sans, cursor: "pointer", letterSpacing: "0.01em",
              display: "inline-flex", alignItems: "center", gap: 8,
            }}
              onMouseEnter={e => e.currentTarget.style.background = T.g600}
              onMouseLeave={e => e.currentTarget.style.background = T.g500}>
              Start free trial
              <Icon name="ArrowRight" size={15} color={T.white} strokeWidth={2} />
            </button>
            <button onClick={goToContact} style={{
              padding: "14px 28px", background: "transparent", color: T.n700,
              border: `1.5px solid ${T.n200}`, borderRadius: 11, fontSize: 15, fontWeight: 600,
              fontFamily: T.sans, cursor: "pointer", letterSpacing: "0.01em",
            }}
              onMouseEnter={e => e.currentTarget.style.borderColor = T.n400}
              onMouseLeave={e => e.currentTarget.style.borderColor = T.n200}>
              Talk to our team
            </button>
          </div>
          <p style={{ fontSize: 12, color: T.n300, margin: "20px 0 0" }}>
            14-day free trial · No credit card required · Cancel any time
          </p>
        </div>

      </div>
    </div>
  );
}